/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 */

// eslint-disable-next-line no-redeclare
/* global console */

import {debounce} from 'lodash-es';
import {HighlightOption} from "@/enum/highlight_option";
import {EventBus} from "@/utils/EventBus";
import {stripMarkers} from "@/utils/string";
import {htmlAfterEditor} from "@/service/analyzeFidelity";
import {captureException} from "@/plugins/Sentry";
import {Store} from "@/plugins/Store";
import {EDITOR_INPUT_DEBOUNCE_MS} from "@/service/editorInputLatency";
import {
    keywordContentStems,
    tokenizeWordsWithOffsets,
    tokenMatchesContentStems,
} from "@/service/keywordStemDensity";

// Trailing only — leading:true used to run highlight + Vuex on the first key.
const INPUT_EVENT_DEBOUNCE_WAIT = EDITOR_INPUT_DEBOUNCE_MS;
const HIGHLIGHT_DEBOUNCE_WAIT = EDITOR_INPUT_DEBOUNCE_MS;

export default {
    name: 'ckeditor',

    render(h) {
        return h(this.tagName);
    },

    model: {
        prop: 'value',
        event: 'input'
    },

    props: {
        editor: {
            type: Function,
            default: null
        },
        value: {
            type: String,
            default: ''
        },
        config: {
            type: Object,
            default: () => ({})
        },
        tagName: {
            type: String,
            default: 'div'
        },
        wordsToHighlight: {
            type: Object, /* <{[key: string]: Array<string> }> */
            default: () => ({})
        },
        hideToolbar: {
            type: Boolean,
            default: false
        },
        multiline: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },

    data: () => ({
        $_instance: null,
        $_lastEditorData: {
            type: String,
            default: ''
        },
        markers: [],
        debouncedInputEvent: null,
        flushDebouncedInput: null
    }),

    mounted() {
        const editorConfig = Object.assign({}, this.config);

        if (this.value) {
            editorConfig.initialData = this.value;
        }

        this.editor.create(this.$el, editorConfig)
            .then(editor => {
                this.$_instance = editor;

                if (this.hideToolbar) {
                    editor.ui.view.toolbar.element.parentElement.style.display = 'none';
                }          
                
                if (this.disabled) {
                    this.$_instance.enableReadOnlyMode('disabled');
                }

                this.$_setUpEditorEvents();

                this.$emit('ready', editor);

                // Set up global highlight colors for marker groups
                for (const markerClass of Object.values(HighlightOption)) {
                    editor.conversion.for('downcast').markerToHighlight({
                        model: `markerHighlight:${markerClass}`,
                        view: {
                            classes: markerClass
                        }
                    });
                }

                if (this.wordsToHighlight && Object.keys(this.wordsToHighlight).length) {
                    this.highlightWords();
                }

            })
            .catch(error => {
                Store.dispatch('displayFlashMessage', {
                    type: 'error',
                    content: 'unknownError'
                });
                captureException(error);
            });
    },

    beforeUnmount() {
        EventBus.$off('debounce-flush', this.flushDebouncedInput);
        if (this.debouncedInputEvent && this.debouncedInputEvent.cancel) {
            this.debouncedInputEvent.cancel();
        }
        if (this.debouncedWordHighlight && this.debouncedWordHighlight.cancel) {
            this.debouncedWordHighlight.cancel();
        }
        if (this.$_instance) {
            this.$_instance.destroy();
            this.$_instance = null;
        }

        this.$emit('destroy', this.$_instance);
    },

    created: function () {
        this.flushDebouncedInput = () => {
            if (!this.debouncedInputEvent) {
                return;
            }
            this.debouncedInputEvent();
            this.debouncedInputEvent.flush();
        };
        EventBus.$on('debounce-flush', this.flushDebouncedInput);
    },

    computed: {
        debouncedWordHighlight: function () {
            return debounce(() => {
                this.highlightWords();
            }, HIGHLIGHT_DEBOUNCE_WAIT);
        }
    },

    watch: {
        value(newValue, oldValue) {
            if (this.$_instance && newValue !== oldValue && newValue !== this.$_lastEditorData) {
                this.$_instance.setData(newValue);
            }
        },

        wordsToHighlight: {
            immediate: true,
            handler: function () {
                if (this.$_instance) {
                    this.debouncedWordHighlight();
                }
            }
        },

        disabled: {
            immediate: true,
            handler: function (newValue) {
                if (this.$_instance) {
                    if (newValue) {
                        this.$_instance.enableReadOnlyMode('disabled');
                    } else {
                        this.$_instance.disableReadOnlyMode('disabled');
                    }
                }
            }
        }
    },

    methods: {
        $_setUpEditorEvents() {
            const editor = this.$_instance;

            // Do not highlight or emit on this tick. 100+ keywords × stem
            // windows / marker walks used to freeze typing for several seconds.
            this.debouncedInputEvent = debounce(() => {
                // Schema used to drop <table> on setData. Restore structured
                // tables from the value that was loaded, then emit that HTML.
                const editorHtml = stripMarkers(editor.getData());
                const content = htmlAfterEditor(this.value, editorHtml);
                if (this.$_lastEditorData !== content) {
                    this.$_lastEditorData = content;
                    this.$emit('input', content);
                }
                this.highlightWords();
            }, INPUT_EVENT_DEBOUNCE_WAIT);

            editor.model.document.on('change:data', this.debouncedInputEvent);

            editor.editing.view.document.on('focus', evt => {
                this.$emit('focus', evt, editor);
            });

            editor.editing.view.document.on('blur', evt => {
                this.flushDebouncedInput();
                this.$emit('blur', evt, editor);
            });

            if (!this.multiline) {
                // Prevent Enter key from creating new lines
                editor.editing.view.document.on('keydown', (evt, data) => {
                    if (data.domEvent.key === 'Enter') {
                        evt.stop();
                        data.preventDefault();
                    }
                });

                // Prevent pasting new lines
                editor.editing.view.document.on('paste', (evt, data) => {
                    const pastedData = data.dataTransfer.getData('text/plain');
                    const modifiedData = pastedData.replace(/\n/g, ' ');

                    evt.stop();
                    data.preventDefault();

                    editor.model.change(writer => {
                        // Getting correct position or selection for pasting
                        const selection = editor.model.document.selection;
                        const range = selection.getFirstRange();
            
                        // Delete the selected text
                        writer.remove(range);
            
                        // Insert the modified text
                        writer.insertText(modifiedData, range.start);
                      });
                });
            }
        },
        highlightWords: function () {
            const editor = this.$_instance;
            if (!editor) {
                return;
            }

            const language = (Store.getters.article && Store.getters.article.language_code) || 'cs';
            const highlightOptions = Object.values(HighlightOption);
            const plans = [];
            let i = 0;
            for (const keyword of Object.keys(this.wordsToHighlight || {})) {
                const markerClass = highlightOptions[i++ % highlightOptions.length];
                const stems = keywordContentStems(keyword, language);
                if (!stems.length) {
                    continue;
                }
                plans.push({stems, markerClass});
            }

            editor.model.change(writer => {
                this.markers.forEach(markerName => writer.removeMarker(markerName));
                this.markers = [];
                if (!plans.length) {
                    return;
                }
                let markerId = 0;
                const range = writer.createRangeIn(editor.model.document.getRoot());
                for (const node of range.getWalker()) {
                    if (node.type !== 'text') {
                        continue;
                    }
                    const text = node.item.data;
                    if (!text) {
                        continue;
                    }
                    const tokens = tokenizeWordsWithOffsets(text);
                    if (!tokens.length) {
                        continue;
                    }
                    const rootEl = node.previousPosition.root;
                    const path = node.previousPosition.path;
                    const parentPath = path.slice(0, -1);
                    const baseOffset = path[path.length - 1];
                    for (const token of tokens) {
                        for (const plan of plans) {
                            if (!tokenMatchesContentStems(token.token, plan.stems, language)) {
                                continue;
                            }
                            this.markers.push(writer.addMarker(`markerHighlight:${plan.markerClass}:${markerId++}`, {
                                range: writer.createRange(
                                    writer.createPositionFromPath(rootEl, parentPath.concat(baseOffset + token.start)),
                                    writer.createPositionFromPath(rootEl, parentPath.concat(baseOffset + token.end))
                                ),
                                usingOperation: false,
                                affectsData: false
                            }));
                        }
                    }
                }
            });
        }
    }
};
